import React, { useState, useEffect } from 'react';
import { Input, Button, Card, Typography, Alert, Space, Form, Select, message, Popover, Spin } from 'antd';
import { QuestionCircleOutlined, CopyOutlined, LinkOutlined } from '@ant-design/icons';
import { VehicleFormData } from '../../types/vehicle';
import { useVehicleData } from '../../hooks/useVehicleData';
import { authPpiApiAxiosWithRefresh } from '../../services/api';

import './web_scraper_custom_ppi.scss';

const { Title, Text } = Typography;
const { Option } = Select;

// Helper function to copy text to clipboard
const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    message.success('VIN copied to clipboard!');
  } catch (err) {
    message.error('Failed to copy VIN');
  }
};

export const VehicleDetailsReviewPage: React.FC<{
  docId: string;
  onNext: (vehicleData: VehicleFormData) => void;
  onBack: () => void;
}> = ({ docId, onNext, onBack }) => {
  const [form] = Form.useForm();
  const { vehicleData, loading, updateVehicleData, isManual } = useVehicleData(docId);
  const [saving, setSaving] = useState(false);
  const [vinLookupLoading, setVinLookupLoading] = useState(false);

  // Watch fields to drive each progressive section
  const watchedMake = Form.useWatch('Make', form);
  const watchedModel = Form.useWatch('Model', form);
  const watchedYear = Form.useWatch('Year', form);
  const watchedEngine = Form.useWatch('Engine', form);
  const watchedTransmission = Form.useWatch('Transmission', form);
  const watchedDrivetrain = Form.useWatch('Drivetrain', form);
  const watchedColor = Form.useWatch('exteriorColor', form);
  const watchedMileage = Form.useWatch('Mileage', form);
  const watchedTitleStatus = Form.useWatch('titleStatus', form);

  // Section 2 (powertrain) appears once identity fields are filled
  const showSection2 = !!(watchedMake?.trim() && watchedModel?.trim() && watchedYear?.toString().trim());
  // Section 3 (condition) appears once any powertrain field is set
  const showSection3 = showSection2 && !!(watchedEngine?.trim() || watchedTransmission || watchedDrivetrain || watchedMileage?.toString().trim());
  // Section 4 (location/notes) appears once color or mileage is entered
  const showSection4 = showSection3 && !!(watchedColor?.trim() || watchedTitleStatus?.trim());

  // Normalize raw NHTSA transmission string → "Automatic" | "Manual" | undefined
  const normalizeTransmission = (raw?: string): string | undefined => {
    if (!raw) return undefined;
    const lower = raw.toLowerCase();
    if (lower.includes('manual') && !lower.includes('automatic')) return 'Manual';
    if (lower.includes('automatic') || lower.includes('auto') || lower.includes('cvt')) return 'Automatic';
    return undefined; // don't set if we can't confidently map it
  };

  // Normalize raw NHTSA drivetrain string → "2WD" | "4WD" | "AWD" | undefined
  const normalizeDrivetrain = (raw?: string): string | undefined => {
    if (!raw) return undefined;
    const lower = raw.toLowerCase();
    if (lower.includes('awd') || lower.includes('all-wheel') || lower.includes('all wheel')) return 'AWD';
    if (lower.includes('4wd') || lower.includes('4x4') || lower.includes('four-wheel') || lower.includes('four wheel')) return '4WD';
    if (lower.includes('2wd') || lower.includes('fwd') || lower.includes('front-wheel') || lower.includes('rwd') || lower.includes('rear-wheel')) return '2WD';
    return undefined;
  };

  const handleVinLookup = async (vin: string) => {
    if (vin.length !== 17) return;
    setVinLookupLoading(true);
    try {
      const api = await authPpiApiAxiosWithRefresh();
      const response = await api.get(`/decode-vin/${vin}`);
      if (response.data.success) {
        const details = response.data.vehicleDetails || {};
        // Normalize select fields so they match our option values exactly
        const transmission = normalizeTransmission(details.Transmission);
        const drivetrain = normalizeDrivetrain(details.Drivetrain);
        form.setFieldsValue({
          VIN: details.VIN ?? vin,
          Make: details.Make,
          Model: details.Model,
          Year: details.Year,
          Engine: details.Engine,
          ...(transmission ? { Transmission: transmission } : {}),
          ...(drivetrain ? { Drivetrain: drivetrain } : {}),
          Trim: details.Trim,
          exteriorColor: details.exteriorColor,
          Mileage: details.Mileage,
          titleStatus: details.titleStatus,
          Location: details.Location,
          description: details.description,
        });
        if (response.data.hasDetails) {
          message.success('Vehicle details populated from VIN!');
        } else {
          message.warning('VIN recognized, but no details were found. Please fill in the fields manually.');
        }
      } else {
        message.error(response.data.error || 'VIN lookup failed.');
      }
    } catch (err: any) {
      message.error(err.response?.data?.error || err.message || 'VIN lookup failed.');
    } finally {
      setVinLookupLoading(false);
    }
  };


  // Update form when vehicle data changes
  useEffect(() => {
    if (vehicleData) {
      form.setFieldsValue(vehicleData);
    }
  }, [vehicleData, form]);

  const handleSubmit = async (values: any) => {
    setSaving(true);
    try {
      await updateVehicleData(values);
      message.success('Vehicle details added successfully');
      // Pass the current vehicle data to parent for vital field checking
      onNext(values);
    } catch (err) {
      message.error('Failed to add vehicle details');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !vehicleData) {
    return (
      <Card className='custom-card'>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Title level={4}>Loading vehicle details...</Title>
        </div>
      </Card>
    );
  }

  return (
    <Card className='custom-card'>
      <Title level={3}>{isManual ? 'Enter Vehicle Details' : 'Review and Confirm'}</Title>
      <Text type="secondary">
        {isManual
          ? 'Enter as much detail as you can. More details will result in a more accurate inspection report.'
          : "We've auto-filled this info from the listing. Please review and make any corrections before continuing."}
      </Text>
      <Alert
        message="💡 These details will power your AI inspection report, so accuracy matters."
        type="warning"
        style={{ marginTop: 24 }}
      />
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={vehicleData || undefined}
        style={{ marginTop: 24 }}
      >
        {/* Group 1: Identity — always visible */}
        <Form.Item
          className="vdr-field vdr-field-vin"
          name="VIN"
          label="VIN"
          extra={vinLookupLoading ? 'Looking up VIN…' : 'Enter a 17-character VIN to auto-fill details'}
        >
          <Input
            placeholder="e.g. 1HGBH41JXMN109186"
            maxLength={17}
            suffix={vinLookupLoading ? <Spin size="small" /> : <span />}
            onChange={(e) => {
              const val = e.target.value.toUpperCase();
              form.setFieldValue('VIN', val);
              handleVinLookup(val);
            }}
          />
        </Form.Item>

        <Form.Item
          className="vdr-field vdr-field-year"
          name="Year"
          label="Year"
          rules={[{ required: true, message: 'Year is required' }]}
        >
          <Input placeholder="e.g. 2021" />
        </Form.Item>

        <Form.Item
          className="vdr-field vdr-field-make"
          name="Make"
          label="Make"
          rules={[{ required: true, message: 'Make is required' }]}
        >
          <Input placeholder="e.g. Toyota" />
        </Form.Item>

        <Form.Item
          className="vdr-field vdr-field-model"
          name="Model"
          label="Model"
          rules={[{ required: true, message: 'Model is required' }]}
        >
          <Input placeholder="e.g. Camry" />
        </Form.Item>

        {/* Section 2: Powertrain — revealed after identity fields filled */}
        <div
          className="vdr-details-section vdr-section-2"
          style={{
            overflow: 'hidden',
            maxHeight: showSection2 ? '800px' : '0px',
            opacity: showSection2 ? 1 : 0,
            transform: showSection2 ? 'translateY(0)' : 'translateY(-8px)',
            transition: 'max-height 0.4s ease, opacity 0.35s ease, transform 0.35s ease',
          }}
        >

          <Form.Item className="vdr-field vdr-field-transmission" name="Transmission" label="Transmission"
            rules={[{ required: true, message: 'Transmission is required' }]}>
            <Select placeholder="Select transmission type">
              <Option value="Automatic">Automatic</Option>
              <Option value="Manual">Manual</Option>
            </Select>
          </Form.Item>

          <Form.Item className="vdr-field vdr-field-drivetrain" name="Drivetrain" label="Drivetrain"
            rules={[{ required: true, message: 'Drivetrain is required' }]}>
            <Select placeholder="Select drivetrain">
              <Option value="2WD">2WD</Option>
              <Option value="4WD">4WD</Option>
              <Option value="AWD">AWD</Option>
            </Select>
          </Form.Item>

          <Form.Item className="vdr-field vdr-field-mileage" name="Mileage" label="Mileage">
            <Input placeholder="e.g. 45000" />
          </Form.Item>

          <Form.Item className="vdr-field vdr-field-trim" name="Trim" label="Trim">
            <Input placeholder="e.g. XSE" />
          </Form.Item>

          <Form.Item className="vdr-field vdr-field-engine" name="Engine" label="Engine">
            <Input placeholder="e.g. 2.5L 4-Cylinder" />
          </Form.Item>


        </div>

        {/* Section 3: Condition & Status — revealed after powertrain touched */}
        <div
          className="vdr-details-section vdr-section-3"
          style={{
            overflow: 'hidden',
            maxHeight: showSection3 ? '800px' : '0px',
            opacity: showSection3 ? 1 : 0,
            transform: showSection3 ? 'translateY(0)' : 'translateY(-8px)',
            transition: 'max-height 0.4s ease, opacity 0.35s ease, transform 0.35s ease',
          }}
        >
          <Form.Item className="vdr-field vdr-field-exterior-color" name="exteriorColor" label="Exterior Color">
            <Input placeholder="e.g. Midnight Black" />
          </Form.Item>

          <Form.Item
            className="vdr-field vdr-field-title-status"
            name="titleStatus"
            label={
              <Space>
                Title Status
                <Popover
                  content={
                    <div style={{ maxWidth: 300 }}>
                      <div>
                        <Text strong>Need help determining title status?</Text>
                        <br />
                        <Text>
                          Check if this vehicle has been reported stolen or salvaged using the NICB database. Copy the VIN below and paste it into the search bar on the NICB website.
                        </Text>
                        <br />
                        <br />
                        <Text>
                          💡 <strong>Tip:</strong> While NICB provides theft/salvage records, a vehicle history report (Carfax) offers more comprehensive title information.
                        </Text>
                      </div>
                      <div style={{ margin: '8px 0', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                        <Text strong>{vehicleData?.VIN || 'VIN not available! You should ask the seller for the VIN.'}</Text>
                      </div>
                      <Space>
                        <Button
                          size="small"
                          icon={<CopyOutlined />}
                          onClick={() => copyToClipboard(vehicleData?.VIN || '')}
                          disabled={!vehicleData?.VIN}
                        >
                          Copy VIN
                        </Button>
                        <Button
                          size="small"
                          type="primary"
                          icon={<LinkOutlined />}
                          onClick={() => window.open('https://www.nicb.org/vincheck', '_blank')}
                        >
                          Check VIN
                        </Button>
                      </Space>
                    </div>
                  }
                  title="Title Status Help"
                  trigger="hover"
                >
                  <QuestionCircleOutlined style={{ color: '#1890ff', cursor: 'pointer', fontSize: '16px' }} />
                </Popover>
              </Space>
            }
          >
            <Select placeholder="Select title status">
              <Option value="Clean">Clean</Option>
              <Option value="Salvage">Salvage</Option>
              <Option value="Rebuilt">Rebuilt</Option>
              <Option value="Parts Only">Parts Only</Option>
            </Select>
          </Form.Item>
        </div>

        {/* Section 4: Location & Notes — revealed after condition fields touched */}
        <div
          className="vdr-details-section vdr-section-4"
          style={{
            overflow: 'hidden',
            maxHeight: showSection4 ? '600px' : '0px',
            opacity: showSection4 ? 1 : 0,
            transform: showSection4 ? 'translateY(0)' : 'translateY(-8px)',
            transition: 'max-height 0.4s ease, opacity 0.35s ease, transform 0.35s ease',
          }}
        >
          <Form.Item className="vdr-field vdr-field-location" name="Location" label="Location">
            <Input placeholder="e.g. Austin, TX" />
          </Form.Item>

          <Form.Item className="vdr-field vdr-field-description" name="description" label="Additional Information">
            <Input.TextArea rows={6} placeholder="Any other details about the vehicle..." />
          </Form.Item>
        </div>

        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Button onClick={onBack} >
            Back
          </Button>
          <Button type="primary" htmlType="submit" loading={saving}>
            Confirm & Continue
          </Button>
        </Space>
      </Form>
    </Card>
  );
};
